@use '@/styles/variables' as *;

// ─── Layout constants ───────────────────────────────────────────────────────

$row-height: 148px;
$card-width: 160px;
$gap: 10px;
$lift: 6px; // card hover translateY — strip padding-top must be >= this

// ─── Shared bar styles (used by .cardSatellite and .cardWide) ────────────────

@mixin skill-bars {
  .barTrack {
    height: 3px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .barFill {
    height: 100%;
    width: 0;
    background: var(--gradient-forest);
    border-radius: 2px;
    // --delay defaults to 0s; wide card sets it per-bar for stagger effect
    transition: width 0.55s cubic-bezier(0.34, 1.2, 0.64, 1) var(--delay, 0s);
  }

  &:hover .barFill {
    // Each .barFill has --level set on itself, so var() resolves to the
    // sub-skill's own value rather than the parent card's level.
    width: calc(var(--level) * 1%);
  }
}

// ─── Wrapper: positions the arrow buttons relative to the strip ─────────────

.wrapper {
  position: relative;
}

// ─── Scrollable strip ───────────────────────────────────────────────────────

.strip {
  overflow-x: auto;
  overflow-y: hidden;
  // padding-top creates room for cards to lift on hover without hitting the
  // overflow-y: hidden clip boundary; margin-top compensates visually
  padding-top: #{$lift};
  margin-top: calc(-1 * #{$lift});
  // shadow-glow: offset 20px + blur 60px = 80px effective reach below card
  padding-bottom: 1.5rem;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in oklch, var(--muted-foreground) 40%, transparent) transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: color-mix(in oklch, var(--muted-foreground) 40%, transparent);
    border-radius: 3px;

    &:hover {
      background: color-mix(in oklch, var(--muted-foreground) 65%, transparent);
    }
  }
}

// ─── Bento track: 2-row CSS grid, columns flow left→right ──────────────────

.track {
  display: grid;
  grid-template-rows: repeat(2, #{$row-height});
  grid-auto-flow: column;
  grid-auto-columns: #{$card-width};
  gap: #{$gap};
  width: max-content;
  list-style: none;
  padding: 0;
  margin: 0;
}

// ─── Base card ──────────────────────────────────────────────────────────────

.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.1rem 1.1rem 1rem;
  display: flex;
  flex-direction: column;
  cursor: default;
  position: relative;
  overflow: hidden;
  transition:
    border-color 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.25s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--gradient-forest);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    border-radius: inherit;
  }

  &:hover {
    border-color: var(--primary);
    box-shadow: var(--shadow-glow);
    transform: translateY(calc(-1 * #{$lift} / 2));

    &::before {
      opacity: 0.04;
    }
  }
}

// ─── Core card (1 col × 2 rows) ─────────────────────────────────────────────

.cardCore {
  grid-row: 1 / -1;

  .coreTag {
    font-size: 0.6rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--primary);
    opacity: 0.7;
    margin-bottom: 0.5rem;
  }

  .name {
    font-size: 1.15rem;
    font-weight: 700;
    font-family: var(--font-display);
    color: var(--card-foreground);
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  .ringWrapper {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-top: 0.5rem;
  }

  .ringLabel {
    position: absolute;
    font-size: 0.85rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--card-foreground);
    opacity: 0;
    transform: scale(0.8);
    transition:
      opacity 0.25s ease 0.45s,
      transform 0.25s ease 0.45s;
    pointer-events: none;
  }

  &:hover .ringLabel {
    opacity: 1;
    transform: scale(1);
  }
}

// ─── Wide core card (2 cols × 2 rows) ────────────────────────────────────────
// Header: name left + ring badge right. Below: 4 full-width bars spread evenly.

.cardWide {
  grid-row: 1 / -1;
  grid-column: span 2;

  @include skill-bars;

  // Header: name group on left, ring badge on right — both pinned to top
  .wideHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  // name inherits .cardCore styles; slight size bump for wider card
  .name {
    font-size: 1.3rem;
  }

  // Ring acts as a compact badge — override .cardCore's flex:1 stretch
  .ringWrapper {
    flex: none;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0;
  }

  .ringLabel {
    position: absolute;
    font-size: 0.85rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--card-foreground);
    opacity: 0;
    transform: scale(0.8);
    transition:
      opacity 0.25s ease 0.45s,
      transform 0.25s ease 0.45s;
    pointer-events: none;
  }

  &:hover .ringLabel {
    opacity: 1;
    transform: scale(1);
  }

  // bars pinned to the bottom of the card
  .subSkillList {
    list-style: none;
    padding: 0;
    margin: auto 0 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .subSkill {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .subSkillMeta {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .subSkillName {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--card-foreground);
  }

  .subSkillPct {
    font-size: 0.68rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--primary);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .subSkillPct {
    opacity: 1;
  }
}

// ─── Satellite card (1 col × 1 row) ─────────────────────────────────────────

.cardSatellite {
  @include skill-bars;

  .name {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--card-foreground);
    letter-spacing: -0.01em;
    line-height: 1.3;
    flex: 1;
  }

  .barMeta {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin-top: auto;
  }

  .barLabel {
    font-size: 0.65rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--primary);
    opacity: 0;
    align-self: flex-end;
    transition: opacity 0.2s ease;
  }

  &:hover .barLabel {
    opacity: 1;
  }
}

// ─── Radial ring (SVG circles) ───────────────────────────────────────────────

.ringTrack {
  fill: none;
  stroke: var(--border);
  stroke-width: 4.5;
}

.ring {
  fill: none;
  stroke: var(--primary);
  stroke-width: 4.5;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  stroke-dasharray: var(--circ);
  stroke-dashoffset: var(--circ);
  transition: stroke-dashoffset 0.85s cubic-bezier(0.34, 1.4, 0.64, 1);
}

.card:hover .ring {
  stroke-dashoffset: calc(var(--circ) * (1 - var(--level) / 100));
}

// ─── Touch devices — auto-reveal graphs (no hover available) ─────────────────

@media (hover: none) {
  @keyframes skillRingFill {
    from {
      stroke-dashoffset: var(--circ);
    }
    to {
      stroke-dashoffset: calc(var(--circ) * (1 - var(--level) / 100));
    }
  }

  @keyframes skillBarFill {
    from {
      width: 0;
    }
    to {
      width: calc(var(--level) * 1%);
    }
  }

  .card .ring {
    animation: skillRingFill 0.9s cubic-bezier(0.34, 1.4, 0.64, 1) 0.4s both;
  }

  .cardSatellite .barFill,
  .cardWide .barFill {
    // --delay is already set per-bar on wide cards; satellite bars get 0s
    animation: skillBarFill 0.6s cubic-bezier(0.34, 1.2, 0.64, 1) calc(0.35s + var(--delay, 0s)) both;
  }

  // Labels visible without hover — scoped to match the specificity of their definitions
  .cardCore .ringLabel,
  .cardWide .ringLabel {
    opacity: 1;
    transform: scale(1);
    transition: none;
  }

  .cardSatellite .barLabel,
  .cardWide .subSkillPct {
    opacity: 1;
    transition: none;
  }
}
